/**
 * Category Splash Screen - JavaScript
 * Version: 1.0.0
 * 
 * Features:
 * - Day/Night Mode Detection
 * - Countdown Timer
 * - Remember User Preference
 * - Floating Particles
 * - Copy Coupon Code
 * - Progress Bar
 * - Smooth Animations
 */

(function() {
  'use strict';

  // Main Splash Controller
  class CategorySplash {
    constructor() {
      this.splash = document.getElementById('category-splash');
      if (!this.splash) return;

      this.config = {
        duration: parseInt(this.splash.dataset.splashDuration) || 0,
        rememberChoice: this.splash.dataset.rememberChoice === 'true',
        dayStart: parseInt(this.splash.dataset.dayStart) || 6,
        nightStart: parseInt(this.splash.dataset.nightStart) || 18,
        enableTimeMode: this.splash.dataset.enableTimeMode === 'true'
      };

      this.storageKey = 'splash_dismissed';
      this.init();
    }

    init() {
      // Check if user has chosen to not see splash
      if (this.shouldSkip()) {
        this.splash.remove();
        return;
      }

      // Initialize components
      this.setupTimeMode();
      this.setupEventListeners();
      this.setupCountdown();
      this.setupParticles();
      this.setupProgressBar();
      this.setupCopyCode();
      
      // Show splash
      this.show();
    }

    shouldSkip() {
      if (!this.config.rememberChoice) return false;
      
      const dismissed = localStorage.getItem(this.storageKey);
      if (!dismissed) return false;

      // Check if still valid (24 hours default)
      const dismissedTime = parseInt(dismissed);
      const now = Date.now();
      const hoursElapsed = (now - dismissedTime) / (1000 * 60 * 60);
      
      // Reset after 24 hours
      if (hoursElapsed > 24) {
        localStorage.removeItem(this.storageKey);
        return false;
      }
      
      return true;
    }

    show() {
      // Add visible class
      this.splash.classList.add('splash-visible');
      document.body.classList.add('splash-active');
      
      // Start auto-close timer if set
      if (this.config.duration > 0) {
        this.autoCloseTimer = setTimeout(() => {
          this.close();
        }, this.config.duration * 1000);
      }
    }

    close() {
      // Clear auto-close timer
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer);
      }

      // Check if "don't show again" is checked
      const dontShowCheckbox = document.getElementById('splash-dont-show');
      if (dontShowCheckbox && dontShowCheckbox.checked) {
        localStorage.setItem(this.storageKey, Date.now().toString());
      }

      // Animate out
      this.splash.classList.add('splash-hiding');
      
      setTimeout(() => {
        this.splash.classList.remove('splash-visible', 'splash-hiding');
        document.body.classList.remove('splash-active');
        this.splash.style.display = 'none';
      }, 500);
    }

    setupTimeMode() {
      if (!this.config.enableTimeMode) return;

      const hour = new Date().getHours();
      const isNight = hour >= this.config.nightStart || hour < this.config.dayStart;

      if (isNight) {
        this.splash.classList.add('night-mode');
      }

      // Update greeting
      const greetingEl = this.splash.querySelector('.time-greeting');
      if (greetingEl) {
        greetingEl.textContent = this.getGreeting(hour);
      }
    }

    getGreeting(hour) {
      if (hour >= 5 && hour < 12) {
        return 'Good Morning!';
      } else if (hour >= 12 && hour < 17) {
        return 'Good Afternoon!';
      } else if (hour >= 17 && hour < 21) {
        return 'Good Evening!';
      } else {
        return 'Good Night!';
      }
    }

    setupEventListeners() {
      // Skip button
      const skipBtn = this.splash.querySelector('.splash-skip-btn');
      if (skipBtn) {
        skipBtn.addEventListener('click', () => this.close());
      }

      // Close button
      const closeBtn = this.splash.querySelector('.splash-close-btn');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      // Category links - close on click
      const categoryLinks = this.splash.querySelectorAll('.splash-category-item');
      categoryLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          // Let the navigation happen, but close splash
          setTimeout(() => this.close(), 100);
        });
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.splash.classList.contains('splash-visible')) {
          this.close();
        }
      });

      // Prevent background scroll
      this.splash.addEventListener('wheel', (e) => {
        const content = this.splash.querySelector('.splash-content');
        const isScrollable = content.scrollHeight > content.clientHeight;
        
        if (!isScrollable) {
          e.preventDefault();
        }
      }, { passive: false });
    }

    setupCountdown() {
      const countdownEl = this.splash.querySelector('.offer-countdown');
      if (!countdownEl) return;

      const endDateStr = countdownEl.dataset.end;
      if (!endDateStr) return;

      const endDate = new Date(endDateStr);
      const timerEl = countdownEl.querySelector('.countdown-timer');
      
      const updateCountdown = () => {
        const now = new Date();
        const diff = endDate - now;

        if (diff <= 0) {
          timerEl.textContent = 'Expired';
          countdownEl.classList.add('expired');
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        let timeString = '';
        if (days > 0) {
          timeString = `${days}d ${hours}h ${minutes}m`;
        } else if (hours > 0) {
          timeString = `${hours}h ${minutes}m ${seconds}s`;
        } else {
          timeString = `${minutes}m ${seconds}s`;
        }

        timerEl.textContent = timeString;
      };

      updateCountdown();
      this.countdownInterval = setInterval(updateCountdown, 1000);
    }

    setupParticles() {
      const particlesContainer = this.splash.querySelector('.splash-particles');
      if (!particlesContainer) return;

      const particleCount = 30;
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'splash-particle';
        
        // Random properties
        const size = Math.random() * 8 + 4;
        const left = Math.random() * 100;
        const delay = Math.random() * 10;
        const duration = Math.random() * 10 + 10;
        const opacity = Math.random() * 0.5 + 0.2;

        particle.style.cssText = `
          width: ${size}px;
          height: ${size}px;
          left: ${left}%;
          animation-delay: ${delay}s;
          animation-duration: ${duration}s;
          opacity: ${opacity};
        `;

        particlesContainer.appendChild(particle);
      }
    }

    setupProgressBar() {
      if (this.config.duration <= 0) return;

      const progressBar = this.splash.querySelector('.splash-progress-bar');
      if (!progressBar) return;

      const duration = this.config.duration * 1000;
      const startTime = Date.now();

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / duration) * 100, 100);
        progressBar.style.width = `${progress}%`;

        if (progress < 100) {
          requestAnimationFrame(updateProgress);
        }
      };

      requestAnimationFrame(updateProgress);
    }

    setupCopyCode() {
      const copyBtns = this.splash.querySelectorAll('.copy-code-btn');
      
      copyBtns.forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          const codeEl = btn.closest('.offer-code');
          const code = codeEl.dataset.code;

          try {
            await navigator.clipboard.writeText(code);
            
            // Visual feedback
            btn.classList.add('copied');
            btn.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            `;

            setTimeout(() => {
              btn.classList.remove('copied');
              btn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              `;
            }, 2000);

            // Show toast notification
            this.showToast('Code copied to clipboard!');
          } catch (err) {
            console.error('Failed to copy:', err);
            
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = code;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            this.showToast('Code copied to clipboard!');
          }
        });
      });
    }

    showToast(message) {
      // Check if toast already exists
      let toast = document.querySelector('.splash-toast');
      
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'splash-toast';
        toast.style.cssText = `
          position: fixed;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%) translateY(100px);
          background: rgba(0, 0, 0, 0.85);
          color: #fff;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          z-index: 1000000;
          transition: transform 0.3s ease;
          backdrop-filter: blur(10px);
        `;
        document.body.appendChild(toast);
      }

      toast.textContent = message;
      
      // Animate in
      requestAnimationFrame(() => {
        toast.style.transform = 'translateX(-50%) translateY(0)';
      });

      // Animate out
      setTimeout(() => {
        toast.style.transform = 'translateX(-50%) translateY(100px)';
        setTimeout(() => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        }, 300);
      }, 2500);
    }

    destroy() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer);
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.categorySplash = new CategorySplash();
    });
  } else {
    window.categorySplash = new CategorySplash();
  }

  // Expose for external control
  window.closeSplash = function() {
    if (window.categorySplash) {
      window.categorySplash.close();
    }
  };

  // Handle page visibility
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && window.categorySplash && window.categorySplash.countdownInterval) {
      // Pause countdown when page is hidden (optional)
    }
  });

})();